// Resident-facing banner shown on /dashboard when an approved resident's
// cycle is within 30 days of ending (or has already ended). Lets them
// toggle continuation_opt_in (and a separate allow_photo_on_continuation).
// Self-fetching — no props.

import { useEffect, useState } from 'react';

interface ResidentRow {
  id: number;
  status: string;
  cycle_end: string | null;
  continuation_opt_in?: string;
  allow_photo_on_continuation?: string;
}

const OPT_LABELS: Record<string, string> = {
  no:        "Don't list publicly",
  crowdfund: "Pool funds for my Mowkin",
  commit:    "Find a volunteer to take it over",
  either:    "Either path is fine",
};

function daysUntil(date: string): number {
  const t = new Date(date + 'T23:59:59').getTime() - Date.now();
  return Math.floor(t / 86400000);
}

export function CycleEndBanner() {
  const [row, setRow] = useState<ResidentRow | null>(null);
  const [optIn, setOptIn] = useState<string>('no');
  const [photoOpt, setPhotoOpt] = useState<string>('no');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/me/dashboard', { credentials: 'include' });
        const j = await res.json() as { ok: boolean; data?: { residents: ResidentRow[] } };
        if (!j.ok) return;
        // Pick the most urgent approved resident with a cycle near end.
        const candidates = (j.data?.residents || []).filter(
          (r) => r.status === 'approved' && r.cycle_end,
        );
        if (candidates.length === 0) return;
        candidates.sort((a, b) => (a.cycle_end || '').localeCompare(b.cycle_end || ''));
        const r = candidates[0];
        if (!r.cycle_end) return;
        const days = daysUntil(r.cycle_end);
        if (days > 30) return;  // Not near end yet.
        setRow(r);
        setOptIn(r.continuation_opt_in || 'no');
        setPhotoOpt(r.allow_photo_on_continuation || 'no');
      } catch { /* swallow */ }
    })();
  }, []);

  const save = async () => {
    if (!row) return;
    setSaving(true); setMsg(null);
    try {
      const res = await fetch('/api/me/continuation', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resident_id: row.id,
          continuation_opt_in: optIn,
          allow_photo_on_continuation: photoOpt,
        }),
      });
      const j = await res.json() as { ok: boolean; error?: string };
      if (!j.ok) throw new Error(j.error || 'Save failed');
      setMsg('Saved.');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Save failed');
    } finally { setSaving(false); }
  };

  if (!row || !row.cycle_end) return null;
  const days = daysUntil(row.cycle_end);
  const ended = days < 0;

  return (
    <section className="bg-cream-alt border-l-4 border-rose p-5 mb-10">
      <div className="text-xs tracking-[0.25em] uppercase text-rose mb-2">
        {ended ? 'Your free cycle has ended' : `Your free cycle ends in ${days} day${days === 1 ? '' : 's'}`}
      </div>
      <h3 className="text-2xl text-forest font-display mb-2">
        Want neighbors to help keep your yard cared for?
      </h3>
      <p className="text-forest/75 text-sm leading-relaxed mb-4 max-w-2xl">
        When the free cycle ends, your household can opt in to two
        continuation paths: neighbors can pool funds to pay your Mowkin
        for another cycle, or a vetted volunteer can sign up to mow on a
        recurring schedule. Your full name and house number stay private.
      </p>

      <div className="grid sm:grid-cols-2 gap-2 mb-4">
        {(['no', 'crowdfund', 'commit', 'either'] as const).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setOptIn(v)}
            className={`text-left px-3 py-2 border text-sm transition-colors ${
              optIn === v
                ? 'border-rose bg-rose/5 text-forest'
                : 'border-forest/20 hover:border-rose text-forest/80'
            }`}
          >
            <div className="text-[10px] tracking-[0.15em] uppercase text-forest/55 mb-0.5">{v}</div>
            <div>{OPT_LABELS[v]}</div>
          </button>
        ))}
      </div>

      {optIn !== 'no' && (
        <div className="mb-4">
          <label className="flex items-start gap-2 text-sm text-forest/80 cursor-pointer">
            <input
              type="checkbox"
              checked={photoOpt === 'yes'}
              onChange={(e) => setPhotoOpt(e.target.checked ? 'yes' : 'no')}
              className="accent-rose mt-1"
            />
            <span>
              Allow yard photos on the public listing.{' '}
              <span className="text-forest/55">
                (Each photo is also reviewed by our admin before it appears.)
              </span>
            </span>
          </label>
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={saving}
          onClick={save}
          className="text-xs tracking-[0.15em] uppercase bg-forest text-cream px-5 py-2 hover:bg-rose transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save my choice'}
        </button>
        {msg && <span className="text-sm text-forest/70">{msg}</span>}
      </div>
    </section>
  );
}
